import type { AxiosRequestConfig } from "axios";

import {
  getZar8UnlockPositionLockedAmount,
  ZAR8_LOCK_DURATION_OPTIONS,
} from "../utils/zar8LockFlow";
import { ApiError, axiosInstance, create, get } from "./apiService";

const BUSINESS_BASE = "/api/v2/business/zar8";

interface TwoFactorInput {
  twoFactorCode?: string;
  twoFactorToken?: string;
}

interface UnlockPosition {
  ledgerId: string;
  [key: string]: unknown;
}

interface BusinessQuery {
  businessId: string;
}

function requireBusinessId(businessId: string | null | undefined): string {
  if (!businessId || typeof businessId !== "string" || !businessId.trim()) {
    throw new Error("A businessId is required for business ZAR8 calls.");
  }
  return businessId.trim();
}

function requirePositionId(positionId: string | null | undefined): string {
  if (!positionId || typeof positionId !== "string" || !positionId.trim()) {
    throw new Error("A positionId is required.");
  }
  return positionId.trim();
}

function withBusinessId<T extends Record<string, unknown>>(
  businessId: string,
  params?: T
): T & BusinessQuery {
  return { ...(params ?? ({} as T)), businessId };
}

export function buildBusinessZar8TwoFactorBody(code: string) {
  return {
    twoFactorCode: code,
    otpCode: code,
    twoFaCode: code,
  };
}

export function normalizeBusinessZar8TwoFactorCode({
  twoFactorCode,
  twoFactorToken,
}: TwoFactorInput = {}): string {
  return twoFactorCode ?? twoFactorToken ?? "";
}

export function buildBusinessZar8TwoFactorConfig(
  code: string,
  baseConfig?: AxiosRequestConfig
): AxiosRequestConfig | undefined {
  if (!code) {
    return baseConfig;
  }
  return {
    ...(baseConfig || {}),
    headers: {
      ...(baseConfig?.headers || {}),
      "X-2FA-Code": code,
    },
  };
}

async function postBusinessZar8WithEnvelope<T = unknown>(
  url: string,
  businessId: string,
  body: Record<string, unknown>,
  code: string,
  extraConfig?: AxiosRequestConfig
): Promise<T> {
  const config = buildBusinessZar8TwoFactorConfig(code, {
    ...(extraConfig || {}),
    params: {
      ...(extraConfig?.params || {}),
      businessId,
    },
  });

  let response;
  try {
    response = await axiosInstance.post(url, body, config);
  } catch (err) {
    if (err instanceof ApiError) {
      throw err;
    }
    const status = (err as { response?: { status?: number } })?.response
      ?.status;
    const data = (err as { response?: { data?: unknown } })?.response?.data;
    if (data && typeof data === "object") {
      const record = data as Record<string, unknown>;
      throw new ApiError(
        (typeof record.message === "string" && record.message) ||
          (typeof record.error === "string" && record.error) ||
          ((err as { message?: string })?.message ?? "Request failed"),
        status,
        record
      );
    }
    throw err;
  }

  const envelope = response?.data as
    | {
        status?: boolean;
        success?: boolean;
        message?: string;
        error?: string;
        data?: unknown;
      }
    | undefined;
  if (envelope && typeof envelope === "object") {
    if (envelope.status === false || envelope.success === false) {
      throw new ApiError(
        envelope.message || envelope.error || "Request failed",
        response.status,
        envelope
      );
    }
    return (envelope.data ?? envelope) as T;
  }
  return envelope as T;
}

export const calculateBusinessZar8Yield = (
  businessId: string,
  params: Record<string, unknown> = {}
) =>
  get(
    `${BUSINESS_BASE}/yield/calculate`,
    withBusinessId(requireBusinessId(businessId), params)
  );

export const getBusinessZar8YieldConfigSettings = (businessId: string) =>
  get(
    `${BUSINESS_BASE}/yield/config`,
    withBusinessId(requireBusinessId(businessId))
  );

export const getBusinessZar8YieldConfig = (businessId: string) =>
  get(
    `${BUSINESS_BASE}/yield-config`,
    withBusinessId(requireBusinessId(businessId))
  );

export const getBusinessZar8YieldPositionsList = async (
  businessId: string,
  params: Record<string, unknown> = {}
) =>
  get(
    `${BUSINESS_BASE}/yield/positions`,
    withBusinessId(requireBusinessId(businessId), params)
  );

export const getBusinessZar8YieldPositionById = (
  businessId: string,
  positionId: string
) =>
  get(
    `${BUSINESS_BASE}/yield/positions/${encodeURIComponent(
      requirePositionId(positionId)
    )}`,
    withBusinessId(requireBusinessId(businessId))
  );

export const getBusinessZar8YieldPositionDistributions = (
  businessId: string,
  positionId: string,
  params: Record<string, unknown> = {}
) =>
  get(
    `${BUSINESS_BASE}/yield/positions/${encodeURIComponent(
      requirePositionId(positionId)
    )}/distributions`,
    withBusinessId(requireBusinessId(businessId), params)
  );

export const getBusinessZar8YieldEarnings = (businessId: string) =>
  get(
    `${BUSINESS_BASE}/yield/earnings`,
    withBusinessId(requireBusinessId(businessId))
  );

export const getBusinessZar8YieldHistory = (
  businessId: string,
  params: Record<string, unknown> = {}
) =>
  get(
    `${BUSINESS_BASE}/yield/history`,
    withBusinessId(requireBusinessId(businessId), params)
  );

export const getBusinessZar8Positions = (
  businessId: string,
  params: Record<string, unknown> = {}
) =>
  get(
    `${BUSINESS_BASE}/positions`,
    withBusinessId(requireBusinessId(businessId), params)
  );

export const getBusinessZar8Position = (
  businessId: string,
  positionId: string
) =>
  get(
    `${BUSINESS_BASE}/positions/${encodeURIComponent(
      requirePositionId(positionId)
    )}`,
    withBusinessId(requireBusinessId(businessId))
  );

interface CreatePositionInput extends TwoFactorInput {
  businessId: string;
  zar8Amount?: string | number;
  amount?: string | number;
  lockPeriod?: string;
  durationId?: string;
  accountId?: string;
}

export const createBusinessZar8Position = async ({
  businessId,
  zar8Amount = "",
  amount = "",
  lockPeriod = "",
  durationId = "",
  accountId = "",
  twoFactorCode,
  twoFactorToken,
}: CreatePositionInput) => {
  const id = requireBusinessId(businessId);
  const code = normalizeBusinessZar8TwoFactorCode({
    twoFactorCode,
    twoFactorToken,
  });
  const body = {
    zar8Amount,
    amount,
    lockPeriod,
    durationId,
    accountId,
    ...buildBusinessZar8TwoFactorBody(code),
  };
  return create(`${BUSINESS_BASE}/positions`, body, {
    ...(buildBusinessZar8TwoFactorConfig(code) || {}),
    params: { businessId: id },
  });
};

export const createBusinessZar8YieldPosition = async ({
  businessId,
  zar8Amount = "",
  amount = "",
  lockPeriod = "",
  durationId = "",
  accountId = "",
  twoFactorCode,
  twoFactorToken,
}: CreatePositionInput) => {
  const id = requireBusinessId(businessId);
  const code = normalizeBusinessZar8TwoFactorCode({
    twoFactorCode,
    twoFactorToken,
  });
  const body = {
    zar8Amount,
    amount,
    lockPeriod,
    durationId,
    accountId,
    ...buildBusinessZar8TwoFactorBody(code),
  };
  return create(`${BUSINESS_BASE}/yield/positions`, body, {
    ...(buildBusinessZar8TwoFactorConfig(code) || {}),
    params: { businessId: id },
  });
};

interface CreateLockInput extends TwoFactorInput {
  businessId: string;
  amount: number | string;
  durationId: string;
  yieldAssetId: string;
  accountId?: string;
}

export const createBusinessZar8Lock = async ({
  businessId,
  amount,
  durationId,
  yieldAssetId,
  accountId = "",
  twoFactorCode,
  twoFactorToken,
}: CreateLockInput) => {
  const id = requireBusinessId(businessId);
  if (!durationId || typeof durationId !== "string" || !durationId.trim()) {
    throw new Error("Select a lock duration to continue.");
  }
  const isValidDuration = ZAR8_LOCK_DURATION_OPTIONS.some(
    (option: { id: string }) => option.id === durationId.trim()
  );
  if (!isValidDuration) {
    throw new Error("Select a valid lock duration to continue.");
  }
  if (
    !yieldAssetId ||
    typeof yieldAssetId !== "string" ||
    !yieldAssetId.trim()
  ) {
    throw new Error("Select a yield asset to continue.");
  }
  const code = normalizeBusinessZar8TwoFactorCode({
    twoFactorCode,
    twoFactorToken,
  });
  const body = {
    amount,
    durationId,
    yieldAssetId,
    accountId,
    ...buildBusinessZar8TwoFactorBody(code),
  };
  return create(`${BUSINESS_BASE}/yield/lock`, body, {
    ...(buildBusinessZar8TwoFactorConfig(code) || {}),
    params: { businessId: id },
  });
};

interface CancelPositionInput extends TwoFactorInput {
  businessId: string;
  positionId: string;
}

export const cancelBusinessZar8YieldPosition = async ({
  businessId,
  positionId,
  twoFactorCode,
  twoFactorToken,
}: CancelPositionInput) => {
  const id = requireBusinessId(businessId);
  const pid = requirePositionId(positionId);
  const code = normalizeBusinessZar8TwoFactorCode({
    twoFactorCode,
    twoFactorToken,
  });
  return postBusinessZar8WithEnvelope(
    `${BUSINESS_BASE}/yield/positions/${encodeURIComponent(pid)}/cancel`,
    id,
    buildBusinessZar8TwoFactorBody(code),
    code
  );
};

export const cancelBusinessZar8Position = async ({
  businessId,
  positionId,
  twoFactorCode,
  twoFactorToken,
}: CancelPositionInput) => {
  const id = requireBusinessId(businessId);
  const pid = requirePositionId(positionId);
  const code = normalizeBusinessZar8TwoFactorCode({
    twoFactorCode,
    twoFactorToken,
  });
  return postBusinessZar8WithEnvelope(
    `${BUSINESS_BASE}/positions/${encodeURIComponent(pid)}/cancel`,
    id,
    buildBusinessZar8TwoFactorBody(code),
    code
  );
};

interface UnlockQuoteInput {
  businessId: string;
  positionId: string;
  amountZar: number;
  selectedPosition?: UnlockPosition | null;
}

export const createBusinessZar8UnlockQuote = async ({
  businessId,
  positionId,
  amountZar,
  selectedPosition,
}: UnlockQuoteInput) => {
  const id = requireBusinessId(businessId);
  const pid = requirePositionId(positionId);

  if (amountZar <= 0) {
    throw new Error("Enter a valid amount to continue.");
  }
  if (selectedPosition) {
    const lockedAmountZar = getZar8UnlockPositionLockedAmount(selectedPosition);
    if (amountZar > lockedAmountZar) {
      throw new Error("Amount exceeds the locked ZAR8 balance.");
    }
  }

  return create(
    `${BUSINESS_BASE}/yield/positions/${encodeURIComponent(pid)}/unlock-quote`,
    { amount: amountZar, zar8Amount: amountZar },
    { params: { businessId: id } }
  );
};

interface UnlockInput extends TwoFactorInput {
  businessId: string;
  amountZar: number;
  selectedPosition: UnlockPosition;
  quote: unknown;
}

export const createBusinessZar8YieldPositionUnlock = async ({
  businessId,
  amountZar,
  selectedPosition,
  quote,
  twoFactorCode,
  twoFactorToken,
}: UnlockInput) => {
  const id = requireBusinessId(businessId);
  if (!selectedPosition) {
    throw new Error("Select an active yield term to continue.");
  }
  const lockedAmountZar = getZar8UnlockPositionLockedAmount(selectedPosition);
  if (amountZar <= 0 || amountZar > lockedAmountZar) {
    throw new Error("Unable to submit this unlock request.");
  }

  const code = normalizeBusinessZar8TwoFactorCode({
    twoFactorCode,
    twoFactorToken,
  });
  const body = {
    zar8Amount: amountZar,
    amount: amountZar,
    quote,
    ...buildBusinessZar8TwoFactorBody(code),
  };
  return postBusinessZar8WithEnvelope(
    `${BUSINESS_BASE}/yield/positions/${encodeURIComponent(
      selectedPosition.ledgerId
    )}/unlock`,
    id,
    body,
    code
  );
};

export const createBusinessZar8PositionUnlock = async ({
  businessId,
  amountZar,
  selectedPosition,
  quote,
  twoFactorCode,
  twoFactorToken,
}: UnlockInput) => {
  const id = requireBusinessId(businessId);
  if (!selectedPosition) {
    throw new Error("Select an active yield term to continue.");
  }
  const lockedAmountZar = getZar8UnlockPositionLockedAmount(selectedPosition);
  if (amountZar <= 0 || amountZar > lockedAmountZar) {
    throw new Error("Unable to submit this unlock request.");
  }

  const code = normalizeBusinessZar8TwoFactorCode({
    twoFactorCode,
    twoFactorToken,
  });
  const body = {
    zar8Amount: amountZar,
    amount: amountZar,
    quote,
    ...buildBusinessZar8TwoFactorBody(code),
  };
  return postBusinessZar8WithEnvelope(
    `${BUSINESS_BASE}/positions/${encodeURIComponent(
      selectedPosition.ledgerId
    )}/unlock`,
    id,
    body,
    code
  );
};

export const getBusinessZar8ErrorEnvelope = (
  error: unknown
): Record<string, unknown> | null => {
  if (!error || typeof error !== "object") return null;
  const r = (error as { response?: unknown }).response;
  if (!r || typeof r !== "object") return null;
  const responseRecord = r as Record<string, unknown>;
  if (
    typeof responseRecord.success === "boolean" ||
    typeof responseRecord.code === "string" ||
    (typeof responseRecord.message === "string" && "success" in responseRecord)
  ) {
    return responseRecord;
  }
  const data = (r as { data?: unknown }).data;
  if (
    data &&
    typeof data === "object" &&
    (typeof (data as Record<string, unknown>).success === "boolean" ||
      typeof (data as Record<string, unknown>).code === "string")
  ) {
    return data as Record<string, unknown>;
  }
  return null;
};
